import { cuesToSrt } from "./srt.js";
import { cleanSubtitleText, extractTextFromRichContent } from "./text.js";
import { fixSpanishCapitalization, hasArtificialCapitalization, isLikelySpanish } from "./capitalization.js";
import type {
  CapCutDraft,
  CapCutDraftMaterial,
  CapCutSegment,
  CapCutTextMaterial,
  CapCutTextTemplate,
  ConversionOptions,
  ConversionResult,
  DraftDiagnostics,
  ResolvedText,
  SourceSummary,
  SubtitleCue
} from "./types.js";

export type * from "./types.js";
export { cuesToSrt } from "./srt.js";
export { cleanSubtitleText, extractTextFromRichContent } from "./text.js";
export { formatSrtTime } from "./time.js";
export { fixSpanishCapitalization, hasArtificialCapitalization, isLikelySpanish, detectProperNouns } from "./capitalization.js";

interface ExtractionContext {
  warnings: string[];
  sources: SourceSummary[];
  visitedDrafts: Set<string>;
  draftHasCapitalStyling: boolean;
  draftIsSpanish: boolean | undefined;
}

interface DraftVisit {
  draft: CapCutDraft;
  path: string;
  offsetUs: number;
}

export function capcutDraftToSrt(input: string | unknown, options: ConversionOptions = {}): ConversionResult {
  const draft = parseDraft(input);
  const diagnostics = analyzeCapCutDraft(draft);
  const context: ExtractionContext = {
    warnings: [],
    sources: [],
    visitedDrafts: new Set(),
    draftHasCapitalStyling: false,
    draftIsSpanish: undefined
  };

  // Pre-scan: detect if draft has capital:"upper" styling at the document level
  // Always run detection so UI can conditionally show the toggle
  context.draftHasCapitalStyling = detectDraftCapitalStyling(draft);

  const cues = extractDraftCues({ draft, path: "root", offsetUs: 0 }, options, context)
    .filter((cue) => cue.endUs > cue.startUs)
    .sort((a, b) => a.startUs - b.startUs || a.endUs - b.endUs)
    .map((cue, index) => ({ ...cue, index: index + 1 }));

  // Post-scan: detect language on combined text if capitalization fix is needed
  if (options.fixCapitalization && context.draftHasCapitalStyling && context.draftIsSpanish === undefined) {
    const combinedText = cues.map((cue) => cue.text).join(" ");
    context.draftIsSpanish = isLikelySpanish(combinedText);
  }

  // Apply capitalization fix to all cues if conditions are met
  if (options.fixCapitalization && context.draftHasCapitalStyling && context.draftIsSpanish) {
    for (let i = 0; i < cues.length; i += 1) {
      const cue = cues[i]!;
      // Only fix cues that are likely Spanish (skip English cues in bilingual drafts)
      if (isLikelySpanish(cue.text) || hasArtificialCapitalization(cue.text)) {
        cues[i] = { ...cue, text: cleanSubtitleText(fixSpanishCapitalization(cue.text)) };
      }
    }
  }

  const dropped = context.sources.reduce((sum, source) => sum + source.subtitleCount, 0) - cues.length;
  if (dropped > 0) {
    context.warnings.push(`${dropped} subtitle cue(s) were ignored because their end time was not after their start time.`);
  }

  const srt = cuesToSrt(cues);
  return {
    srt,
    cues,
    subtitleCount: cues.length,
    warnings: context.warnings,
    sources: context.sources,
    diagnostics,
    hasCapitalStyling: context.draftHasCapitalStyling,
  };
}

export function analyzeCapCutDraft(input: string | unknown): DraftDiagnostics {
  const root = parseDraft(input);
  const diagnostics: DraftDiagnostics = {
    draftCount: 0,
    textTrackCount: 0,
    textSegmentCount: 0,
    textMaterialCount: 0,
    textTemplateCount: 0,
    nestedDraftCount: 0,
    candidateDraftPaths: []
  };
  const visited = new WeakSet<object>();

  visitUnknown(root, "root", (value, path) => {
    if (!isObject(value) || visited.has(value)) {
      return;
    }

    if (isDraftCandidate(value)) {
      visited.add(value);
      diagnostics.draftCount += 1;
      diagnostics.candidateDraftPaths.push(path);

      const draft = value as CapCutDraft;
      const textTracks = (draft.tracks ?? []).filter((track) => track.type === "text");
      diagnostics.textTrackCount += textTracks.length;
      diagnostics.textSegmentCount += textTracks.reduce((sum, track) => sum + (track.segments?.length ?? 0), 0);
      diagnostics.textMaterialCount += draft.materials?.texts?.length ?? 0;
      diagnostics.textTemplateCount += draft.materials?.text_templates?.length ?? 0;
      diagnostics.nestedDraftCount += draft.materials?.drafts?.filter((item) => item.draft).length ?? 0;
    }
  });

  return diagnostics;
}

function parseDraft(input: string | unknown): CapCutDraft {
  if (typeof input !== "string") {
    return input as CapCutDraft;
  }

  try {
    return JSON.parse(input) as CapCutDraft;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Unknown JSON parse error";
    throw new Error(`Invalid CapCut JSON: ${message}`);
  }
}

function isDraftCandidate(value: Record<string, unknown>): boolean {
  return Array.isArray(value.tracks) || isObject(value.materials);
}

function visitUnknown(value: unknown, path: string, visitor: (value: unknown, path: string) => void): void {
  visitor(value, path);

  if (Array.isArray(value)) {
    value.forEach((item, index) => visitUnknown(item, `${path}[${index}]`, visitor));
    return;
  }

  if (!isObject(value)) {
    return;
  }

  for (const [key, child] of Object.entries(value)) {
    visitUnknown(child, `${path}.${key}`, visitor);
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object";
}

function extractDraftCues(visit: DraftVisit, options: ConversionOptions, context: ExtractionContext): SubtitleCue[] {
  const draftKey = `${visit.path}:${visit.draft.id ?? "no-id"}:${visit.offsetUs}`;
  if (context.visitedDrafts.has(draftKey)) {
    return [];
  }
  context.visitedDrafts.add(draftKey);

  const localCues = extractTextTrackCues(visit, options, context);
  context.sources.push({
    draftName: visit.draft.name || visit.draft.id || visit.path,
    path: visit.path,
    subtitleCount: localCues.length
  });

  const childCues = getNestedDraftVisits(visit).flatMap((childVisit) =>
    extractDraftCues(childVisit, options, context)
  );

  return [...localCues, ...childCues];
}

function extractTextTrackCues(visit: DraftVisit, options: ConversionOptions, context: ExtractionContext): SubtitleCue[] {
  const tracks = Array.isArray(visit.draft.tracks) ? visit.draft.tracks : [];
  const cues: SubtitleCue[] = [];

  for (const track of tracks) {
    if (track.type !== "text") {
      continue;
    }

    for (const segment of track.segments ?? []) {
      const segmentCues = resolveSegmentCues(visit, segment, options, context);
      cues.push(...segmentCues);
    }
  }

  return cues;
}

function resolveSegmentCues(
  visit: DraftVisit,
  segment: CapCutSegment,
  options: ConversionOptions,
  context: ExtractionContext
): SubtitleCue[] {
  const timerange = segment.target_timerange;
  const start = visit.offsetUs + numeric(timerange?.start);
  const duration = numeric(timerange?.duration);
  const end = start + duration;

  if (!segment.material_id) {
    context.warnings.push(`Text segment in ${visit.path} has no material_id and was skipped.`);
    return [];
  }

  const resolved = resolveTextForSegment(visit.draft, segment.material_id);
  if (!resolved) {
    context.warnings.push(`Text material ${segment.material_id} in ${visit.path} could not be resolved.`);
    return [];
  }

  if (options.mode === "word") {
    const wordCues = wordsToCues(resolved.material, start, visit.path);
    if (wordCues.length > 0) {
      return wordCues;
    }
  }

  const text = cleanSubtitleText(resolved.text);
  if (!text) {
    context.warnings.push(`Text material ${segment.material_id} in ${visit.path} was empty and was skipped.`);
    return [];
  }

  return [{ startUs: start, endUs: end, text, sourcePath: visit.path }];
}

function resolveTextForSegment(draft: CapCutDraft, materialId: string): ResolvedText | undefined {
  const texts = indexById(draft.materials?.texts);
  const templates = indexById(draft.materials?.text_templates);

  const directText = texts.get(materialId);
  if (directText) {
    const text = pickTextMaterialContent(directText);
    return {
      text,
      material: directText,
      strategy: "direct-text",
      hasCapitalStyling: hasArtificialCapitalization(text)
    };
  }

  const template = templates.get(materialId);
  if (!template) {
    return undefined;
  }

  const visibleResources = (template.text_info_resources ?? [])
    .filter((resource) => resource.clip_type !== "can_not_render");

  const hasCapitalStyling = visibleResources.some((resource) => resource.capital === "upper");

  const resourceTexts = resolveTemplateTextMaterials(template, texts);
  const text = resourceTexts.map(pickTextMaterialContent).filter(Boolean).join("\n");
  return {
    text: text || template.merge_content || "",
    material: resourceTexts[0],
    strategy: "text-template",
    hasCapitalStyling
  };
}

function resolveTemplateTextMaterials(
  template: CapCutTextTemplate,
  textMap: Map<string, CapCutTextMaterial>
): CapCutTextMaterial[] {
  return (template.text_info_resources ?? [])
    .filter((resource) => resource.clip_type !== "can_not_render")
    .map((resource) => resource.text_material_id ? textMap.get(resource.text_material_id) : undefined)
    .filter((material): material is CapCutTextMaterial => Boolean(material));
}

/**
 * Pre-scans the entire draft to detect if any template uses capital:"upper" styling,
 * or if direct-text materials show artificial capitalization patterns.
 */
function detectDraftCapitalStyling(draft: CapCutDraft): boolean {
  const templates = draft.materials?.text_templates ?? [];

  // Check templates for capital:"upper"
  for (const template of templates) {
    const visibleResources = (template.text_info_resources ?? [])
      .filter((r) => r.clip_type !== "can_not_render");
    if (visibleResources.some((r) => r.capital === "upper")) {
      return true;
    }
  }

  // For direct-text drafts, sample a few materials to detect artificial patterns
  const texts = draft.materials?.texts ?? [];
  if (texts.length > 0 && templates.length === 0) {
    let artificialCount = 0;
    const sampleSize = Math.min(texts.length, 30);
    for (let i = 0; i < sampleSize; i += 1) {
      const content = pickTextMaterialContent(texts[i]!);
      if (hasArtificialCapitalization(content)) {
        artificialCount += 1;
      }
    }
    // At least 1 material with clear artificial capitalization
    return artificialCount >= 1;
  }

  // Check nested drafts recursively
  for (const draftMaterial of draft.materials?.drafts ?? []) {
    if (draftMaterial.draft && detectDraftCapitalStyling(draftMaterial.draft)) {
      return true;
    }
  }

  return false;
}

function pickTextMaterialContent(material: CapCutTextMaterial): string {
  return (
    material.recognize_text ||
    extractTextFromRichContent(material.content) ||
    material.base_content ||
    ""
  );
}

function wordsToCues(material: CapCutTextMaterial | undefined, segmentStartUs: number, sourcePath: string): SubtitleCue[] {
  if (!material?.words) {
    return [];
  }

  if (Array.isArray(material.words)) {
    return material.words
      .map((word) => ({
        startUs: segmentStartUs + numeric(word.begin),
        endUs: segmentStartUs + numeric(word.end),
        text: cleanSubtitleText(word.text ?? ""),
        sourcePath
      }))
      .filter((cue) => cue.text.length > 0);
  }

  const starts = material.words.start_time ?? [];
  const ends = material.words.end_time ?? [];
  const texts = material.words.text ?? [];
  const cues: SubtitleCue[] = [];

  for (let index = 0; index < texts.length; index += 1) {
    const text = cleanSubtitleText(texts[index] ?? "");
    if (!text) {
      continue;
    }

    cues.push({
      startUs: segmentStartUs + millisecondsToMicroseconds(numeric(starts[index])),
      endUs: segmentStartUs + millisecondsToMicroseconds(numeric(ends[index])),
      text,
      sourcePath
    });
  }

  return cues;
}

function getNestedDraftVisits(parent: DraftVisit): DraftVisit[] {
  const nestedDrafts = Array.isArray(parent.draft.materials?.drafts) ? parent.draft.materials.drafts : [];
  if (nestedDrafts.length === 0) {
    return [];
  }

  return nestedDrafts.flatMap((material, index) => {
    if (!material.draft) {
      return [];
    }

    const offsets = findDraftInsertionOffsets(parent.draft, material);
    const usableOffsets = offsets.length > 0 ? offsets : [parent.offsetUs];

    return usableOffsets.map((offset, offsetIndex) => ({
      draft: material.draft as CapCutDraft,
      path: `${parent.path}/drafts[${index}]${offsets.length > 1 ? `#${offsetIndex + 1}` : ""}`,
      offsetUs: offset
    }));
  });
}

function findDraftInsertionOffsets(parentDraft: CapCutDraft, material: CapCutDraftMaterial): number[] {
  if (!material.id) {
    return [];
  }

  const offsets: number[] = [];
  for (const track of parentDraft.tracks ?? []) {
    for (const segment of track.segments ?? []) {
      const refs = [segment.material_id, ...(segment.extra_material_refs ?? [])];
      if (refs.includes(material.id)) {
        offsets.push(numeric(segment.target_timerange?.start));
      }
    }
  }
  return offsets;
}

function indexById<T extends { id?: string }>(items: T[] | undefined): Map<string, T> {
  const map = new Map<string, T>();
  for (const item of items ?? []) {
    if (item.id) {
      map.set(item.id, item);
    }
  }
  return map;
}

function numeric(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function millisecondsToMicroseconds(milliseconds: number): number {
  return milliseconds * 1000;
}
